import React, { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Link, useSearchParams } from "react-router-dom";
import {
  BookOpen, Rocket, User, Shield, Printer, ScanLine, Key, LifeBuoy,
  Download, Search, ArrowLeft, FileDown,
} from "lucide-react";
import { api, API, BRAND } from "../lib/api";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CallButton from "../components/CallButton";
import WhatsAppButton from "../components/WhatsAppButton";

const ICONS = {
  book: BookOpen, rocket: Rocket, user: User, shield: Shield,
  printer: Printer, "scan-line": ScanLine, key: Key, "life-buoy": LifeBuoy,
};

const AUDIENCE_STYLE = {
  everyone:  { label: "Everyone",   bg: "#faf7ed", fg: "#7a5a00" },
  customer:  { label: "Customer",   bg: "#e8f3ee", fg: "#1B3022" },
  admin:     { label: "Admin",      bg: "#f4e8e8", fg: "#7a1b1b" },
  warehouse: { label: "Warehouse",  bg: "#eef0f8", fg: "#2a2a6a" },
};

const HelpPage = () => {
  const [params, setParams] = useSearchParams();
  const activeKey = params.get("doc") || "QUICK_START";
  const [docs, setDocs] = useState([]);
  const [markdown, setMarkdown] = useState("");
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/docs");
        setDocs(data.docs || []);
      } catch (e) {
        console.error("Failed to load docs list", e);
      }
    })();
  }, []);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/docs/${activeKey}`, { responseType: "text" });
        setMarkdown(typeof data === "string" ? data : String(data));
      } catch (e) {
        setMarkdown(`# Document not found\n\nKey: \`${activeKey}\``);
      } finally {
        setLoading(false);
      }
    })();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeKey]);

  const filtered = useMemo(() => {
    if (!q.trim()) return docs;
    const needle = q.toLowerCase();
    return docs.filter(
      (d) => d.title.toLowerCase().includes(needle) || d.key.toLowerCase().includes(needle),
    );
  }, [docs, q]);

  const activeDoc = docs.find((d) => d.key === activeKey);

  const pdfUrl = (key) => `${API}/docs/${key}/pdf`;

  return (
    <div className="min-h-screen bg-[#F5EFE0] flex flex-col" data-testid="help-page">
      <Navbar />

      {/* Hero strip */}
      <div
        className="border-b-2"
        style={{
          borderColor: "#D4AF37",
          background: "linear-gradient(135deg, #0F1D14 0%, #1B3022 100%)",
        }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
          <div className="flex items-center gap-3 text-[#D4AF37]/80 text-xs tracking-[0.3em] uppercase">
            <Link to="/" className="hover:text-[#D4AF37] flex items-center gap-2" data-testid="help-back-home">
              <ArrowLeft size={14} /> Back
            </Link>
            <span>·</span>
            <span>Documentation Center</span>
          </div>
          <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-serif-bv text-[#D4AF37]">
            Help &amp; User Manuals
          </h1>
          <p className="mt-3 text-[#F5EFE0]/85 max-w-2xl text-base">
            Official documentation for the {BRAND.name} platform — customer guides, administrator manual, hardware setup, and troubleshooting.
          </p>

          <a
            href={pdfUrl("HBS_MANUAL_COMPLETE")}
            target="_blank"
            rel="noreferrer"
            data-testid="download-complete-pdf"
            className="mt-6 inline-flex items-center gap-2 bg-[#D4AF37] text-[#0F1D14] px-5 py-3 rounded-sm text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#E8C857] transition-colors"
          >
            <FileDown size={16} />
            Download Complete Manual (PDF)
          </a>
        </div>
      </div>

      {/* Body: sidebar + content */}
      <div className="flex-1 max-w-7xl mx-auto w-full px-4 lg:px-10 py-10 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8">
        {/* Sidebar */}
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#1B3022]/50" size={16} />
            <input
              data-testid="help-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search docs…"
              className="w-full bg-white border border-[#D4AF37]/40 pl-9 pr-3 py-2 rounded-sm text-sm focus:outline-none focus:border-[#D4AF37]"
            />
          </div>
          <nav className="space-y-1" data-testid="help-doc-list">
            {filtered.map((d) => {
              const Icon = ICONS[d.icon] || BookOpen;
              const isActive = d.key === activeKey;
              const aud = AUDIENCE_STYLE[d.audience] || AUDIENCE_STYLE.everyone;
              return (
                <button
                  key={d.key}
                  data-testid={`help-nav-${d.key}`}
                  onClick={() => setParams({ doc: d.key })}
                  className={`w-full text-left flex items-start gap-3 px-3 py-2.5 rounded-sm border transition-colors ${
                    isActive
                      ? "bg-[#1B3022] border-[#D4AF37] text-[#F5EFE0]"
                      : "bg-white/70 border-transparent hover:border-[#D4AF37]/40 text-[#1B3022]"
                  }`}
                >
                  <Icon size={18} className={isActive ? "text-[#D4AF37] mt-0.5" : "text-[#1B3022]/70 mt-0.5"} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{d.title}</div>
                    <span
                      className="mt-1 inline-block text-[9px] tracking-[0.15em] uppercase px-1.5 py-0.5 rounded-sm"
                      style={{ background: aud.bg, color: aud.fg }}
                    >
                      {aud.label}
                    </span>
                  </div>
                </button>
              );
            })}
            {filtered.length === 0 && (
              <div className="text-sm text-[#1B3022]/60 px-3 py-4">No docs match “{q}”.</div>
            )}
          </nav>
        </aside>

        {/* Content */}
        <article
          data-testid="help-doc-content"
          className="bg-white border border-[#D4AF37]/30 rounded-sm shadow-sm p-6 lg:p-10 min-h-[60vh]"
        >
          {activeDoc && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#D4AF37]/30 pb-4 mb-6">
              <div>
                <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37]">Documentation</div>
                <h2 className="text-2xl font-serif-bv text-[#1B3022]">{activeDoc.title}</h2>
              </div>
              <a
                href={pdfUrl(activeDoc.key)}
                target="_blank"
                rel="noreferrer"
                data-testid={`download-pdf-${activeDoc.key}`}
                className="inline-flex items-center gap-2 border border-[#D4AF37] text-[#1B3022] px-4 py-2 rounded-sm text-xs uppercase tracking-[0.2em] hover:bg-[#D4AF37] hover:text-[#0F1D14] transition-colors"
              >
                <Download size={14} />
                PDF
              </a>
            </div>
          )}

          {loading ? (
            <div className="animate-pulse space-y-3">
              <div className="h-6 bg-[#F5EFE0] w-1/2 rounded" />
              <div className="h-4 bg-[#F5EFE0] w-3/4 rounded" />
              <div className="h-4 bg-[#F5EFE0] w-2/3 rounded" />
              <div className="h-4 bg-[#F5EFE0] w-5/6 rounded" />
            </div>
          ) : (
            <div className="prose-hbs">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>
            </div>
          )}
        </article>
      </div>

      <Footer />
      <CallButton />
      <WhatsAppButton />
    </div>
  );
};

export default HelpPage;
